import React, { useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaFacebook, FaInstagram } from 'react-icons/fa';
import { useBooking } from '../../contexts/BookingContext';
import { useSiteInfo } from '../../contexts/SiteInfoContext';
import { servicesAPI } from '../../services/api';
import logoImage from '../../assets/images/logo.png';
import { normalizePhoneNumbers, getAddressLines, getContactEmail, getAccreditationInfo } from '../../utils/businessInfo';
import './Footer.css';

const fallbackServices = [
  { name: 'Minor Service' },
  { name: 'Major Service' },
  { name: 'Brake & Suspension' },
  { name: 'Aircon Service' },
  { name: 'Tyres & Alignment' },
];

const unwrapList = (payload) => {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload.results)) {
    return payload.results;
  }
  return [];
};

const Footer = () => {
  const [footerServices, setFooterServices] = useState([]);
  const currentYear = new Date().getFullYear();
  const { openBookingModal } = useBooking();
  const { siteInfo } = useSiteInfo();

  const company = siteInfo?.company ?? {};
  const businessInfo = siteInfo?.business_info ?? {};
  const phoneNumbers = useMemo(
    () => normalizePhoneNumbers(businessInfo, company),
    [businessInfo, company]
  );
  const addressLines = useMemo(
    () => getAddressLines(businessInfo, company),
    [businessInfo, company]
  );
  const contactEmail = getContactEmail(businessInfo, company);
  const accreditation = getAccreditationInfo(businessInfo, siteInfo?.site_settings ?? {});
  const { logo: vaccLogoUrl, link: vaccLink, text: vaccText } = accreditation;
  // Check for special closure
  const specialClosure = businessInfo.special_closure;
  const isClosedToday = specialClosure && specialClosure.type === 'full_day';

  const footerHoursText = isClosedToday
    ? specialClosure.message || 'Closed today'
    : businessInfo.hours_summary ||
      siteInfo?.site_settings?.['footer.hours'] ||
      'Mon-Thu & Sat: 9:30 AM - 5:30 PM';

  const footerDescription =
    siteInfo?.site_settings?.['footer.description'] ||
    'Your trusted motor mechanic workshop in Laverton North since 2017. Expert service, competitive prices, honest workmanship.';
  const hideVaccLogo = (event) => {
    const wrapper = event.currentTarget.closest('.footer-bottom-vacc');
    if (wrapper) {
      wrapper.style.display = 'none';
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadFooterServices = async () => {
      try {
        const response = await servicesAPI.getAllServices();
        if (!isMounted) {
          return;
        }
        setFooterServices(unwrapList(response.data));
      } catch (error) {
        console.error('Failed to load footer services', error);
      }
    };

    loadFooterServices();

    return () => {
      isMounted = false;
    };
  }, []);

  const facebookLink = siteInfo?.site_settings?.['social.facebook'] || 'https://www.facebook.com';
  const instagramLink = siteInfo?.site_settings?.['social.instagram'] || 'https://www.instagram.com';

  const servicesToDisplay = footerServices.length > 0 ? footerServices : fallbackServices;

  return (
    <footer className="footer">
      <div className="footer-main">
        <div className="container">
          <div className="footer-grid">
            {/* Company Info */}
            <div className="footer-column">
              <img
                src={logoImage}
                alt="Automotive Care logo"
                className="footer-logo"
                loading="lazy"
                width={220}
                height={90}
              />
              <h3 className="footer-heading">{company.name || 'Automotive Care'}</h3>
              <p className="footer-text">{footerDescription}</p>
              <div className="footer-social">
                <a href={facebookLink} target="_blank" rel="noopener noreferrer" className="social-link facebook" aria-label="Facebook">
                  <FaFacebook />
                </a>
                <a href={instagramLink} target="_blank" rel="noopener noreferrer" className="social-link instagram" aria-label="Instagram">
                  <FaInstagram />
                </a>
              </div>
              {/* VACC Accreditation */}
              {vaccLogoUrl && (
                <div className="footer-vacc-inline">
                  {vaccLink ? (
                    <a
                      href={vaccLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="VACC accreditation"
                    >
                      <img
                        src={vaccLogoUrl}
                        alt="VACC Accredited Member"
                        loading="lazy"
                        onError={hideVaccLogo}
                      />
                    </a>
                  ) : (
                    <img
                      src={vaccLogoUrl}
                      alt="VACC Accredited Member"
                      loading="lazy"
                      onError={hideVaccLogo}
                    />
                  )}
                  {vaccText && <p>{vaccText}</p>}
                </div>
              )}
            </div>

            {/* Quick Links */}
            <div className="footer-column">
              <h4 className="footer-heading">Quick Links</h4>
              <ul className="footer-links">
                <li><Link to="/">Home</Link></li>
                <li><Link to="/about">About Us</Link></li>
                <li><Link to="/services">Our Services</Link></li>
                <li>
                  <button
                    type="button"
                    className="footer-link-button"
                    onClick={openBookingModal}
                  >
                    Book Appointment
                  </button>
                </li>
                <li><Link to="/contact">Contact Us</Link></li>
                <li><Link to="/sitemap">Sitemap</Link></li>
              </ul>
            </div>

            {/* Services */}
            <div className="footer-column">
              <h4 className="footer-heading">Our Services</h4>
              <ul className="footer-links">
                {servicesToDisplay.map((service) => {
                  const name = service?.name || service?.label || 'Service';
                  const slug = service?.slug;
                  const linkTo = slug ? `/services/${slug}` : '/services';
                  const key = slug || name;

                  return (
                    <li key={key}>
                      <Link to={linkTo}>{name}</Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Contact Info */}
            <div className="footer-column">
              <h4 className="footer-heading">Contact Us</h4>
              <ul className="footer-contact">
                <li>
                  <FaMapMarkerAlt />
                  <span>
                    {addressLines.length > 0
                      ? addressLines.map((line, index) => (
                          <React.Fragment key={`${line}-${index}`}>
                            {line}
                            <br />
                          </React.Fragment>
                        ))
                      : '2A Supertron Court, Laverton North, VIC 3026'}
                  </span>
                </li>
                <li>
                  <FaPhone />
                  <span>
                    {phoneNumbers.length > 0 ? (
                      phoneNumbers.map((phone, index) => (
                        <React.Fragment key={`${phone.number}-${index}`}>
                          <a href={`tel:${phone.number.replace(/\s+/g, '')}`}>{phone.number}</a>
                          {index < phoneNumbers.length - 1 && <br />}
                        </React.Fragment>
                      ))
                    ) : (
                      <>
                        <a href="tel:0383682249">(03) 8368 2249</a>
                        <br />
                        <a href="tel:0405491459">0405 491 459</a>
                      </>
                    )}
                  </span>
                </li>
                <li>
                    <FaEnvelope />
                    <span>
                      <a href={`mailto:${contactEmail || 'info@automotivecare.com.au'}`}>
                        {contactEmail || 'info@automotivecare.com.au'}
                      </a>
                    </span>
                  </li>
                <li>
                  <FaClock />
                  <span style={isClosedToday ? { color: '#ff6b6b', fontWeight: 'bold' } : {}}>
                    {isClosedToday && '🔴 '}
                    {footerHoursText}
                  </span>
                </li>
              </ul>
            </div>

          </div>
      </div>
    </div>

    {/* Footer Bottom */}
    <div className="footer-bottom">
      <div className="container">
        <div className="footer-bottom-content">
          <div className="footer-bottom-info">
            <p>&copy; {currentYear} Automotive Care. All rights reserved.</p>
          </div>
          <p className="footer-license">
            {company.license_number || 'Licensed Automotive Air Conditioning Service Provider (AU License: AU55615)'}
            {company.abn && (
              <>
                <br />
                <span className="footer-abn">ABN {company.abn}</span>
              </>
            )}
          </p>
        </div>
        <div className="footer-credit">
          <p>
            Designed and developed by{' '}
            <a href="https://codeteki.au/" target="_blank" rel="noopener noreferrer" className="codeteki-link">
              CODETEKI
            </a>
          </p>
        </div>
      </div>
    </div>
  </footer>
  );
};

export default Footer;
